import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/disc.png';
import {Activity, BarChart3, FileText, LineChart, Calculator, Users} from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/filings');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/filing-delta-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/filing-language-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/financials');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/financial-bridge-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const FinancialsPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="SEC Filings API – 10-K, 10-Q & Financial Statements"
        description="SEC filings API for 10-K, 10-Q and 8-K with normalized statements: income, balance sheet, cash flow and ratios. One API for fundamental analysis."
        canonicalUrl="https://axionquant.com/products/disclosures"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion SEC Filings API",
          "description": "SEC filings API for 10-K, 10-Q, and 8-K from EDGAR, plus normalized financial statements - income statement, balance sheet, cash flow, and financial ratios. For fundamental analysis and quantitative research.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "SEC Filings & Financial Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>

      <Header
  docs="/docs/03-fundamentals/financials"
  title="SEC Filings API: 10-K, 10-Q & Financial Statements"
  body="Access the full spectrum of corporate financial data - from raw SEC filings like 10-K and 10-Q to normalized income statements, balance sheets, cash flow, and key ratios. Built for quants, fundamental analysts, and systematic investors."
  crumb={{name: 'Disclosures', path:'/products/disclosures'}}
  tabs={[
    {
  label: 'Financial Statements',
  language: 'python',
  filename: 'statements.py',
  bg: '5.jpg',
  code: `from axion import Axion

client = Axion(api_key="your_api_key")
ticker = "MSFT"

balance = client.financials.balance_sheet(
    ticker,
    year=2025,
    quarter=2,
)

income = client.financials.income_statement(
    ticker,
    year=2025,
    quarter=2,
)

cashflow = client.financials.cash_flow(
    ticker,
    year=2025,
    quarter=2,
)

print(balance, income, cashflow)
`,
},
{
  label: 'Income & Cash Flow',
  language: 'python',
  filename: 'income_cashflow.py',
  bg: '5.jpeg',
  code: `from axion import Axion, utils, visualize

client  = Axion(api_key="your_api_key")
ticker  = "AAPL"
periods = 8

df_income = utils.df(
    client.financials.net_income(ticker, periods=periods)
)

df_ocf = utils.df(
    client.financials.operating_cash_flow(ticker, periods=periods)
)

df = utils.stitch([df_income, df_ocf], col="date")

visualize.graph(
    df,
    x="date",
    bars=["revenue"],
    lines=["net_income", "operating_cash_flow"],
    title=f"{ticker} - Income & Cash Flow (8 Quarters)",
)


`,
},
    {
      label: 'SEC Filings Monitor',
      language: 'python',
      filename: 'sec_filings_monitor.py',
      bg: '8.jpeg',
      code: `
from axion import Axion, utils

client = Axion(api_key="your_api_key")

watchlist = [ "AAPL", "MSFT", "GOOGL", "TSLA", "META", ]
forms = { "last_10k": "10-K", "last_10q": "10-Q", "recent_8k": "8-K", }
filings_map = {}

def fetch_filings(row):
    ticker = row["ticker"]

    latest = {
        label: client.filings.by_form(
            ticker, form_type=form, limit=1
        )
        for label, form in forms.items()
    }

    return ( ticker, {
            "ticker": ticker,
            **{ label: filings[0]["filing_date"] for label, filings in latest.items() },
        },
    )

utils.work(
    utils.df( [{"ticker": ticker} for ticker in watchlist]),
    fetch_filings,
    filings_map,
)

df_filings = utils.df(list(filings_map.values()))
`,
    },
    {
      label: 'Cross-Sector Snapshot',
      language: 'python',
      filename: 'cross_sector_snapshot.py',
      bg: '22.jpg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["AAPL", "MSFT", "JPM", "XOM", "JNJ", "AMZN", "NVDA"]

def fetch_snapshot(row):
    t    = row["ticker"]
    snap = client.financials.snapshot(t)
    met  = client.financials.metrics(t)
    return (t, {
        "ticker":       t,
        "gross_margin": met["grossMargin"],
        "roe":          met["returnOnEquity"],
        "debt_equity":  met["debtToEquity"],
        "fcf":          snap["freeCashFlow"],
    })

snapshot_map = {}
utils.work(utils.df([{"ticker": t} for t in tickers]), fetch_snapshot, snapshot_map)

df_snap = utils.df(list(snapshot_map.values()))

visualize.fit(df_snap, x="gross_margin", y="roe", hover=["ticker", "debt_equity", "fcf"])
`,
    },
  ]}
/>
      <Marquee />

      <FeaturesGrid
        title="SEC Filings API. "
        subtitle="SEC data, financial statements, and regulatory filings. "
        features={[
        {
          fig: 'FIG 0.1',
          icon: <FileText size={80} color="var(--purple)" />,
          title: 'SEC Filings (EDGAR)',
          description: 'Complete access to all SEC filings: 10-K, 10-Q, 8-K, 4, 13F, DEF 14A, and more. Search by ticker, form type, year, and quarter. Direct URLs to original documents.',
        },
        {
          fig: 'FIG 0.2',
          icon: <BarChart3 size={80} color="var(--green)" />,
          title: 'Balance Sheet',
          description: 'Annual total assets, current assets, total liabilities, current liabilities, and stockholders\' equity. Multi-year periods (1-5 years) for trend analysis.',
        },
        {
          fig: 'FIG 0.3',
          icon: <LineChart size={80} color="var(--red)" />,
          title: 'Income Statement',
          description: 'Annual revenue and net income figures. Normalized and inflation-adjusted, ready for ratio calculations and growth analysis.',
        },
        {
          fig: 'FIG 0.4',
          icon: <Activity size={80} color="var(--purple)" />,
          title: 'Cash Flow Statement',
          description: 'Operating cash flow, capital expenditures, and free cash flow. Understand cash generation and capital allocation over time.',
        },
        {
          fig: 'FIG 0.5',
          icon: <Calculator size={80} color="var(--green)" />,
          title: 'Financial Ratios',
          description: 'Current ratio, debt-to-assets, debt-to-equity, ROA, ROE, profit margins, EBITDA margins, and more - computed directly from as-reported figures.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Users size={80} color="var(--red)" />,
          title: 'Shares & Valuation',
          description: 'Basic and diluted weighted-average shares outstanding. Combined with price snapshot for P/E, P/B, and market cap.',
        },
      ]} />

      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Global Financial Coverage"
        headingPlain="SEC filings data"
        headingEm="for 60,000+ companies, back to 1987."
        body="SEC EDGAR (US), SEDAR (Canada), and major global exchanges. Access standardized financial statements, SEC filing metadata, and derived metrics - all normalized into a single SEC data API."
        stats={[
          { v: "60K+",   l: "Companies"       },
          { v: "35+ yr", l: "History"         },
          { v: "500K+",  l: "Filings indexed" },
          { v: "24h",    l: "Daily Updates"   },
        ]}
        markers={[
          // North America
          { id: "nyse",        location: [40.7069, -74.0113],  label: "NYSE" },
          { id: "nasdaq",      location: [40.7560, -73.9857],  label: "NASDAQ" },
          { id: "sec",         location: [38.8951, -77.0364],   label: "SEC EDGAR" },
          { id: "tsx",         location: [43.6487, -79.3803],   label: "Toronto Stock Exchange" },
          { id: "cboe",        location: [41.8789, -87.6359],   label: "CBOE" },
          { id: "ice",         location: [33.7490, -84.3880],   label: "ICE Exchange" },
          { id: "otcmarkets",  location: [40.7128, -74.0060],   label: "OTC Markets" },
          { id: "mexbol",      location: [19.4326, -99.1332],   label: "Mexican Stock Exchange" },

          // Europe
          { id: "lse",         location: [51.5155, -0.0993],    label: "London Stock Exchange" },
          { id: "euronext",    location: [52.3731, 4.8952],     label: "Euronext" },
          { id: "deutsche",    location: [50.1138, 8.6798],     label: "Xetra / Deutsche Börse" },
          { id: "frankfurt",   location: [50.1109, 8.6821],     label: "Frankfurt Exchange" },
          { id: "six",         location: [47.3719, 8.5404],     label: "SIX Swiss Exchange" },
          { id: "borsa",       location: [45.4642, 9.1900],     label: "Borsa Italiana" },
          { id: "bme",         location: [40.4168, -3.7038],    label: "BME Spain" },
          { id: "euronextparis",location: [48.8566, 2.3522],    label: "Euronext Paris" },
          { id: "oslo",        location: [59.9139, 10.7522],    label: "Oslo Børs" },
          { id: "nasdaqnordic",location: [59.3293, 18.0686],    label: "Nasdaq Nordic" },
          { id: "warsaw",      location: [52.2297, 21.0122],    label: "Warsaw Stock Exchange" },

          // Asia Pacific
          { id: "tse",         location: [35.6825, 139.7731],   label: "Tokyo Stock Exchange" },
          { id: "sse",         location: [31.2361, 121.5063],   label: "Shanghai Stock Exchange" },
          { id: "szse",        location: [22.5392, 114.0497],   label: "Shenzhen Stock Exchange" },
          { id: "hkex",        location: [22.2796, 114.1612],   label: "Hong Kong Exchange" },
          { id: "krx",         location: [37.5226, 127.0453],   label: "Korea Exchange" },
          { id: "bse",         location: [18.9292, 72.8339],    label: "Bombay Stock Exchange" },
          { id: "nseindia",    location: [19.0728, 72.8826],    label: "National Stock Exchange India" },
          { id: "asx",         location: [-33.8625, 151.2105],  label: "Australian Securities Exchange" },
          { id: "sgx",         location: [1.2834, 103.8519],    label: "Singapore Exchange" },
          { id: "idx",         location: [-6.1754, 106.8272],   label: "Indonesia Stock Exchange" },
          { id: "set",         location: [13.7563, 100.5018],   label: "Thailand Stock Exchange" },
          { id: "bursa",       location: [3.1390, 101.6869],    label: "Bursa Malaysia" },
          { id: "twse",        location: [25.0330, 121.5654],   label: "Taiwan Stock Exchange" },
          { id: "nzx",         location: [-36.8485, 174.7633],  label: "New Zealand Exchange" },

          // Middle East / Africa
          { id: "tadawul",     location: [24.7136, 46.6753],    label: "Saudi Tadawul" },
          { id: "adx",         location: [24.4539, 54.3773],    label: "Abu Dhabi Securities Exchange" },
          { id: "dfm",         location: [25.2048, 55.2708],    label: "Dubai Financial Market" },
          { id: "jse",         location: [-26.2041, 28.0473],   label: "Johannesburg Stock Exchange" },
          { id: "egx",         location: [30.0444, 31.2357],    label: "Egyptian Exchange" },
          { id: "nseafrica",   location: [-1.2864, 36.8172],    label: "Nairobi Securities Exchange" },

          // Financial Disclosure / Regulatory Data Centers
          { id: "sec-edgar",   location: [38.8951, -77.0364],   label: "SEC EDGAR Filings" },
          { id: "fred",        location: [38.6270, -90.1994],   label: "Federal Reserve FRED" },
          { id: "federalreserve",location: [38.8951, -77.0364], label: "Federal Reserve" },
          { id: "ecb",         location: [50.1109, 8.6821],     label: "European Central Bank" },
          { id: "bankofengland",location: [51.5142, -0.0886],   label: "Bank of England" },
          { id: "boj",         location: [35.6762, 139.6503],   label: "Bank of Japan" },
          { id: "pbc",         location: [39.9042, 116.4074],   label: "People's Bank of China" },
          { id: "rbi",         location: [18.9388, 72.8354],    label: "Reserve Bank of India" },
          { id: "bis",         location: [47.5547, 7.5926],     label: "Bank for International Settlements" },

          // Accounting / Corporate Disclosure Hubs
          { id: "iasb",        location: [51.5074, -0.1278],    label: "IASB IFRS Foundation" },
          { id: "fasb",        location: [39.1031, -84.5120],   label: "FASB Accounting Standards" },
          { id: "pcaob",       location: [38.8951, -77.0364],   label: "PCAOB Audit Oversight" },
          { id: "gaap",        location: [40.7128, -74.0060],   label: "US GAAP Reporting" },

          // Data Providers / Market Intelligence
          { id: "bloomberg",   location: [40.7549, -73.9840],   label: "Bloomberg" },
          { id: "refinitiv",   location: [51.5074, -0.1278],    label: "Refinitiv" },
          { id: "spglobal",    location: [40.7143, -74.0060],   label: "S&P Global" },
          { id: "moodys",      location: [40.7580, -73.9855],   label: "Moody's" },
          { id: "morningstar", location: [41.8781, -87.6298],   label: "Morningstar" },
        ]}
      />

      <DataGrid
        title="Complete SEC Filings & Financial Statement Coverage"
        subtitle="From raw SEC filings like 10-K and 10-Q to ready-to-use income statements, balance sheets, cash flows, and derived ratios. One SEC data API for fundamental analysis across all public companies."
        bg={databg}
        stats={[
          { label: 'Companies', value: '60,000+' },
          { label: 'Financial Statements', value: '3M+' },
          { label: 'SEC Filings Indexed', value: '20M+' },
          { label: 'Metrics / Ratios', value: '15+ types' },
        ]}
      />

      <CardList
        title="Engineered for Fundamental Research"
        vid={'https://cdn.studio14a.com/axion/products/news.mp4'} // replace if needed

        points={[
          {
            "title": "SEC Filings API",
            "details": "List available form types with descriptions. Retrieve recent 10-K, 10-Q, and 8-K filings by ticker, filter by form, year, quarter. Get direct EDGAR URLs and accession numbers.",
            "cta": "Explore filings",
            "url": "/docs"
          },
          {
            "title": "Financial Statements",
            "details": "Annual total assets, current assets, liabilities, equity, revenue, net income, operating cash flow, CapEx, and free cash flow. Request 1-5 years of history.",
            "cta": "View balance sheet",
            "url": "/docs"
          },
          {
            "title": "Ratios & Snapshot",
            "details": "Pre-computed liquidity, leverage, profitability, and valuation ratios. Plus a daily snapshot with price, targets, margins, and growth rates.",
            "cta": "See financial snapshot",
            "url": "/products/stocks"
          }
        ]}
      />

      <Responses
        heading="Every Disclosure, One Endpoint Away"
        subheading="From top-line revenue to balance sheet detail, cash flow, share counts, and point-in-time valuation - ready to query in a single request."
        ticker="AAPL"
        tabs={[
          {
            title: 'Financial Snapshot',
            description: 'Daily market snapshot - current price, analyst targets, margins, growth rates, and valuation ratios, refreshed each trading day.',
            data: {
              currentPrice: 272.19,
              targetHighPrice: 350,
              targetLowPrice: 215,
              targetMeanPrice: 287.70682,
              targetMedianPrice: 300,
              recommendationMean: 2,
              recommendationKey: 'buy',
              numberOfAnalystOpinions: 41,
              totalCash: 54697000960,
              totalCashPerShare: 3.702,
              ebitda: 144748003328,
              totalDebt: 112377004032,
              quickRatio: 0.771,
              currentRatio: 0.893,
              totalRevenue: 416161005568,
              debtToEquity: 152.411,
              revenuePerShare: 27.84,
              returnOnAssets: 0.22964,
              returnOnEquity: 1.7142199,
              grossProfits: 195201007616,
              freeCashflow: 78862254080,
              operatingCashflow: 111482003456,
              earningsGrowth: 0.912,
              revenueGrowth: 0.079,
              grossMargins: 0.46905,
              ebitdaMargins: 0.34782,
              operatingMargins: 0.31647,
              profitMargins: 0.26915002,
              financialCurrency: 'USD'
            },
          },
          {
            title: 'Financial Metrics',
            description: 'A single-call composite of the latest fiscal year - revenue, income, balance sheet, cash flow, and derived ratios in camelCase.',
            data: {
              revenue: 143756000000,
              netIncome: 42097000000,
              totalAssets: 379297000000,
              totalLiabilities: 291107000000,
              stockholdersEquity: 88190000000,
              currentAssets: 158104000000,
              currentLiabilities: 162367000000,
              operatingCashFlow: 53925000000,
              capitalExpenditures: 2373000000,
              freeCashFlow: 51552000000,
              sharesOutstandingBasic: 14748158000,
              sharesOutstandingDiluted: 14810356000,
              currentRatio: 0.973744664864166,
              debtToAssets: 0.76749091081659
            },
          },
          {
            title: 'Balance Sheet',
            description: 'Annual balance sheet items including total assets, current assets, liabilities, and stockholders\' equity.',
            data: [
              {
                fiscalYear: '2024',
                totalAssets: 379297000000,
                currentAssets: 158104000000,
                totalLiabilities: 291107000000,
                currentLiabilities: 162367000000,
                stockholdersEquity: 88190000000
              },
              {
                fiscalYear: '2023',
                totalAssets: 352583000000,
                currentAssets: 143566000000,
                totalLiabilities: 290437000000,
                currentLiabilities: 137296000000,
                stockholdersEquity: 62146000000
              },
              {
                fiscalYear: '2022',
                totalAssets: 352755000000,
                currentAssets: 135405000000,
                totalLiabilities: 302083000000,
                currentLiabilities: 153982000000,
                stockholdersEquity: 50672000000
              }
            ],
          },
          {
            title: 'Income Statement',
            description: 'Annual income statement items including revenue, cost of goods sold, operating expenses, and net income.',
            data: [
              {
                fiscalYear: '2024',
                revenue: 416161000000,
                costOfRevenue: 216919000000,
                grossProfit: 199242000000,
                operatingExpenses: 13962000000,
                operatingIncome: 119437000000,
                netIncome: 112010000000
              },
              {
                fiscalYear: '2023',
                revenue: 383285000000,
                costOfRevenue: 201717000000,
                grossProfit: 181568000000,
                operatingExpenses: 12805000000,
                operatingIncome: 108949000000,
                netIncome: 99603000000
              },
              {
                fiscalYear: '2022',
                revenue: 394328000000,
                costOfRevenue: 223546000000,
                grossProfit: 170782000000,
                operatingExpenses: 12879000000,
                operatingIncome: 119437000000,
                netIncome: 99803000000
              }
            ],
          },
          {
            title: 'Cash Flow Statement',
            description: 'Annual cash flow statement items including operating cash flow, capital expenditures, and free cash flow.',
            data: [
              {
                fiscalYear: '2024',
                operatingCashFlow: 111482000000,
                capitalExpenditures: 13717000000,
                freeCashFlow: 97765000000,
                investingCashFlow: -8440000000,
                financingCashFlow: -112165000000
              },
              {
                fiscalYear: '2023',
                operatingCashFlow: 105522000000,
                capitalExpenditures: 10903000000,
                freeCashFlow: 94619000000,
                investingCashFlow: -7240000000,
                financingCashFlow: -100860000000
              },
              {
                fiscalYear: '2022',
                operatingCashFlow: 122271000000,
                capitalExpenditures: 10042000000,
                freeCashFlow: 112229000000,
                investingCashFlow: -15650000000,
                financingCashFlow: -105888000000
              }
            ],
          },

          {
            title: 'Recent Filings',
            description: 'The latest SEC filings for a ticker, including form type, filing date, and direct EDGAR links.',
            data: {
              "company": {
                "name": "Apple Inc.",
                "cik": 320193,
                "ticker": "AAPL"
              },
              "filings": [
                {
                  "cik": 320193,
                  "form": "4",
                  "filingDate": "2026-01-15",
                  "accessionNumber": "000032019326000015",
                  "documentId": "aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMTUvaW5kZXguaHRtbA==",
                  "url": "https://www.sec.gov/Archives/edgar/data/320193/000032019326000015-index.html"
                },
                {
                  "cik": 320193,
                  "form": "10-Q",
                  "filingDate": "2026-01-12",
                  "accessionNumber": "000032019326000006",
                  "documentId": "aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMDYvYWFwbC0yMDI1MTIyNy5odG0=",
                  "url": "https://www.sec.gov/Archives/edgar/data/320193/000032019326000006/aapl-20251227.htm"
                },
                {
                  "cik": 320193,
                  "form": "8-K",
                  "filingDate": "2026-01-10",
                  "accessionNumber": "000032019326000005",
                  "documentId": "aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMDUvYWFwbC0yMDI2MDEyOS5odG0=",
                  "url": "https://www.sec.gov/Archives/edgar/data/320193/000032019326000005/aapl-20260129.htm"
                }
              ]
            },
          },
          {
            title: 'Filing Sentiment',
            description: 'Aggregated sentiment and key-topic breakdown for a company\'s recent SEC filings.',
            data: {
              "label": "NEGATIVE",
              "score": 0.984021182571139,
              "breakdown": {
                "positive": {
                  "count": 63,
                  "avgScore": 0.987096269925435
                },
                "negative": {
                  "count": 293,
                  "avgScore": 0.984021182571139
                }
              }
            },
          },
          {
            title: 'Discounted Cashflow Analysis',
            description: 'Estimated intrinsic value including fair value, margin of safety, and weighted average cost of capital.',
            data: {
              value: {
                "enterpriseValue": 3953331149665.99,
                "cash": 68507000000,
                "totalDebt": 111088000000,
                "equityValue": 3910750149665.99,
                "sharesOutstanding": 14687356000,
                "fairPrice": 266.27,
                "currentPrice": 315.32,
                "marginOfSafety": -0.18,
                "recommendation": "Sell"
              }, 
              rate: {
                "marketCap": 4631217093920,
                "beta5y": 1.0839,
                "totalDebt": 111088000000,
                "interestExpense": 1002000000,
                "pretaxIncome": 26998000000,
                "taxProvision": 4042000000,
                "riskFreeRate": 0.0353,
                "weightOfDebt": 0.0234,
                "weightOfEquity": 0.9766,
                "costOfDebt": 0.009,
                "costOfEquity": 0.1054,
                "taxRate": 0.15,
                "wacc": 0.1031
              }


            },
          },
          {
            title: 'Historical Valuation Metrics',
            description: 'Daily and quarterly historical valuation data, including EPS, P/E, market cap, ROE, enterprise value, EBITDA, and debt-to-equity.',
            data: {
              eps: [
                { reportDate: '2024-12-31', tailingEps: 6.29 },
                { reportDate: '2025-03-31', tailingEps: 6.41 },
                { reportDate: '2025-06-30', tailingEps: 6.58 },
                { reportDate: '2025-09-30', tailingEps: 7.46 },
                { reportDate: '2025-12-31', tailingEps: 7.9 },
                { reportDate: '2026-03-31', tailingEps: 8.27 }
              ],
              pe: [
                { reportDate: '2026-07-06', epsReportDate: '2026-03-31', closePrice: 312.66, ttmEps: 8.27, ttmPe: 37.82 },
                { reportDate: '2026-07-07', epsReportDate: '2026-03-31', closePrice: 310.66, ttmEps: 8.27, ttmPe: 37.58 },
                { reportDate: '2026-07-08', epsReportDate: '2026-03-31', closePrice: 313.39, ttmEps: 8.27, ttmPe: 37.91 },
                { reportDate: '2026-07-09', epsReportDate: '2026-03-31', closePrice: 316.22, ttmEps: 8.27, ttmPe: 38.25 },
                { reportDate: '2026-07-10', epsReportDate: '2026-03-31', closePrice: 315.32, ttmEps: 8.27, ttmPe: 38.14 },
                { reportDate: '2026-07-13', epsReportDate: '2026-03-31', closePrice: 317.31, ttmEps: 8.27, ttmPe: 38.38 }
              ],
              marketcap: [
                { reportDate: '2026-07-02', sharesReportDate: '2026-04-17', closePrice: 308.63, sharesOutstanding: 14687356000, marketCapitalization: 4532958682280 },
                { reportDate: '2026-07-06', sharesReportDate: '2026-04-17', closePrice: 312.66, sharesOutstanding: 14687356000, marketCapitalization: 4592148726960 },
                { reportDate: '2026-07-07', sharesReportDate: '2026-04-17', closePrice: 310.66, sharesOutstanding: 14687356000, marketCapitalization: 4562774014960 },
                { reportDate: '2026-07-08', sharesReportDate: '2026-04-17', closePrice: 313.39, sharesOutstanding: 14687356000, marketCapitalization: 4602870496840 },
                { reportDate: '2026-07-09', sharesReportDate: '2026-04-17', closePrice: 316.22, sharesOutstanding: 14687356000, marketCapitalization: 4644435714320 },
                { reportDate: '2026-07-10', sharesReportDate: '2026-04-17', closePrice: 315.32, sharesOutstanding: 14687356000, marketCapitalization: 4631217093920 },
                { reportDate: '2026-07-13', sharesReportDate: '2026-04-17', closePrice: 317.31, sharesOutstanding: 14687356000, marketCapitalization: 4660444932360 }
              ],
              roe: [
                { reportDate: '2024-12-31', netIncomeCommonStockholders: 36330000000, beginningStockholdersEquity: 56950000000, endingStockholdersEquity: 66758000000, avgEquity: 61854000000, roe: 0.5874 },
                { reportDate: '2025-03-31', netIncomeCommonStockholders: 24780000000, beginningStockholdersEquity: 66758000000, endingStockholdersEquity: 66796000000, avgEquity: 66777000000, roe: 0.3711 },
                { reportDate: '2025-06-30', netIncomeCommonStockholders: 23434000000, beginningStockholdersEquity: 66796000000, endingStockholdersEquity: 65830000000, avgEquity: 66313000000, roe: 0.3534 },
                { reportDate: '2025-09-30', netIncomeCommonStockholders: 27466000000, beginningStockholdersEquity: 65830000000, endingStockholdersEquity: 73733000000, avgEquity: 69781500000, roe: 0.3936 },
                { reportDate: '2025-12-31', netIncomeCommonStockholders: 42097000000, beginningStockholdersEquity: 73733000000, endingStockholdersEquity: 88190000000, avgEquity: 80961500000, roe: 0.52 },
                { reportDate: '2026-03-31', netIncomeCommonStockholders: 29578000000, beginningStockholdersEquity: 88190000000, endingStockholdersEquity: 106491000000, avgEquity: 97340500000, roe: 0.3039 }
              ],
              ev: [
                { reportDate: '2026-06-24', marketCapitalization: 4304570296480, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4343709296480 },
                { reportDate: '2026-06-25', marketCapitalization: 4041226003400, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4080365003400 },
                { reportDate: '2026-06-26', marketCapitalization: 4167977885680, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4207116885680 },
                { reportDate: '2026-06-29', marketCapitalization: 4138015679440, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4177154679440 },
                { reportDate: '2026-06-30', marketCapitalization: 4249933332160, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4289072332160 },
                { reportDate: '2026-07-01', marketCapitalization: 4323663859280, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4362802859280 },
                { reportDate: '2026-07-02', marketCapitalization: 4532958682280, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4572097682280 },
                { reportDate: '2026-07-06', marketCapitalization: 4592148726960, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4631287726960 },
                { reportDate: '2026-07-07', marketCapitalization: 4562774014960, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4601913014960 },
                { reportDate: '2026-07-08', marketCapitalization: 4602870496840, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4642009496840 },
                { reportDate: '2026-07-09', marketCapitalization: 4644435714320, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4683574714320 },
                { reportDate: '2026-07-10', marketCapitalization: 4631217093920, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4670356093920 },
                { reportDate: '2026-07-13', marketCapitalization: 4660444932360, totalDebt: 84711000000, cashAndCashEquivalents: 45572000000, enterpriseValue: 4699583932360 }
              ],
              ebitda: [
                { reportDate: '2025-06-30', ttmEbitda: 141696000000, reportDateEbitda: { '2024-09-30': 32502000000, '2024-12-31': 45912000000, '2025-03-31': 32250000000, '2025-06-30': 31032000000 }, exchangeToUsdRate: 1, ttmEbitdaUsd: 141696000000 },
                { reportDate: '2025-09-30', ttmEbitda: 144748000000, reportDateEbitda: { '2024-12-31': 45912000000, '2025-03-31': 32250000000, '2025-06-30': 31032000000, '2025-09-30': 35554000000 }, exchangeToUsdRate: 1, ttmEbitdaUsd: 144748000000 },
                { reportDate: '2025-12-31', ttmEbitda: 152902000000, reportDateEbitda: { '2025-03-31': 32250000000, '2025-06-30': 31032000000, '2025-09-30': 35554000000, '2025-12-31': 54066000000 }, exchangeToUsdRate: 1, ttmEbitdaUsd: 152902000000 },
                { reportDate: '2026-03-31', ttmEbitda: 159976000000, reportDateEbitda: { '2025-06-30': 31032000000, '2025-09-30': 35554000000, '2025-12-31': 54066000000, '2026-03-31': 39324000000 }, exchangeToUsdRate: 1, ttmEbitdaUsd: 159976000000 }
              ],
              de: [
                { reportDate: '2024-09-30', totalDebt: 106629000000, stockholdersEquity: 56950000000, debtToEquity: 1.87 },
                { reportDate: '2024-12-31', totalDebt: 96799000000, stockholdersEquity: 66758000000, debtToEquity: 1.45 },
                { reportDate: '2025-03-31', totalDebt: 98186000000, stockholdersEquity: 66796000000, debtToEquity: 1.47 },
                { reportDate: '2025-06-30', totalDebt: 101698000000, stockholdersEquity: 65830000000, debtToEquity: 1.54 },
                { reportDate: '2025-09-30', totalDebt: 98657000000, stockholdersEquity: 73733000000, debtToEquity: 1.34 },
                { reportDate: '2025-12-31', totalDebt: 90509000000, stockholdersEquity: 88190000000, debtToEquity: 1.03 },
                { reportDate: '2026-03-31', totalDebt: 84711000000, stockholdersEquity: 106491000000, debtToEquity: 0.8 }
              ]
            },
          },

          {
            title: 'Historical Series',
            description: 'Quarterly values for every major corporate financial statement line item.',
            data: {
              totalAssets: [
                379297000000,
                359241000000,
                359241000000,
                331495000000
              ],
              netIncome: [
                42097000000,
                112010000000,
                84544000000,
                23434000000
              ],
              revenue: [
                143756000000,
                416161000000,
                313695000000,
                94036000000
              ],
              operatingCashflow: [
                53925000000,
                111482000000,
                81754000000,
                53887000000
              ],
              capEx: [
                2373000000,
                12715000000,
                9473000000,
                6011000000
              ],
              sharesOutstanding: [
                14748158000,
                14948500000,
                14992898000,
                14902886000
              ],
              stockholdersEquity: [
                88190000000,
                73733000000,
                73733000000,
                65830000000
              ],
              totalLiabilities: [
                291107000000,
                285508000000,
                285508000000,
                265665000000
              ]
            },
          },
        ]}
      />

      <DatasetDetails full={true}
        title="SEC Filings & Financial Statement Coverage"
        subtitle="Access daily SEC EDGAR filings, normalized financial statements, pre-computed ratios, and share data for 60,000+ public companies worldwide. Full historical depth back to 1987, retrievable nearly instantly. Built for quantitative factor research, fundamental screeners, and automated valuation models. We parse and standardize every filing from raw XBRL to clean JSON."
        cta="Start Free"
        ctaLink="/login"
        tiles={[
          {
            img: '6.webp',
            title: 'We cover 60,000+ companies',
            caption: 'You can access SEC filings and financial statements for public companies worldwide.',
            cn: 'scaled corner',
            tile: <W1/>
          },
          {
            img: '4.webp',
            title: 'We provide 600,000+ annual statements',
            caption: 'You get normalized balance sheets, income statements, and cash flow statements ready for analysis.',
            cn: 'scaled corner',
            tile: <W2/>
          },
          {
            img: '9.webp',
            title: 'We index 20M+ SEC filings',
            caption: 'You can search and retrieve every 10-K, 10-Q, 8-K, and more with direct audit trails.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '7.webp',
            title: 'We offer 35+ years of history',
            caption: 'You can backtest fundamental strategies back to 1987 with multi-period data.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '2.webp',
            title: 'We deliver in under 24 hours',
            caption: 'You receive clean, structured JSON data within a day of SEC filing release.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />
      
      <Outcomes
        headline="Numbers that speak for themselves."
        subheadline="Institutional-grade SEC data and financial statements trusted by quantitative funds, fundamental investors, and fintech platforms."
        leftCell={{ stat: '20M+', title: '20M+ filings indexed.', desc: 'Every 10-K, 10-Q, 8-K, 4, and more - searchable and linkable.', showChart: true }}
        rightCells={[
          { stat: '24h', title: 'Daily Updates.', desc: 'New filings and financial statements within 24 hours of SEC release.' },
          { stat: '35+YR', title: '35+ Years of History.', desc: 'Backtest fundamental strategies back to 1987.', boldDesc: true },
        ]}
      /><ProductFAQ slug="disclosures" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default FinancialsPage;
